/** Shared problem-page keyboard shortcuts (LeetCode-style). */

export const isMacPlatform = () => {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.userAgentData?.platform || navigator.platform || '';
  return /Mac|iPhone|iPad|iPod/i.test(platform);
};

export const modKey = () => (isMacPlatform() ? '⌘' : 'Ctrl');
export const altKey = () => (isMacPlatform() ? '⌥' : 'Alt');

/** Visual key chips for settings UI */
export const PROBLEM_SHORTCUTS = [
  {
    id: 'run',
    action: 'Run code',
    keys: () => [modKey(), "'"],
  },
  {
    id: 'submit',
    action: 'Submit',
    keys: () => [modKey(), '⏎'],
  },
  {
    id: 'close-tab',
    action: 'Hide test panel',
    keys: () => [altKey(), 'W'],
  },
  {
    id: 'maximize',
    action: 'Maximize / Exit maximize panel',
    keys: () => [altKey(), '+'],
  },
  {
    id: 'fullscreen',
    action: 'Enter / Exit full screen',
    keys: () => [altKey(), 'F'],
  },
];

/** Same bindings, shown/wired against whatever actions that surface actually has. */
export const IDE_SHORTCUTS = [
  {
    id: 'run',
    action: 'Run code',
    keys: () => [modKey(), "'"],
  },
  {
    id: 'close-tab',
    action: 'Hide / show console',
    keys: () => [altKey(), 'W'],
  },
  {
    id: 'fullscreen',
    action: 'Enter / Exit full screen',
    keys: () => [altKey(), 'F'],
  },
];

export const LIVE_CODING_SHORTCUTS = [
  {
    id: 'run',
    action: 'Run code',
    keys: () => [modKey(), "'"],
  },
  {
    id: 'close-tab',
    action: 'Hide / show output',
    keys: () => [altKey(), 'W'],
  },
  {
    id: 'fullscreen',
    action: 'Enter / exit full screen',
    keys: () => [altKey(), 'F'],
  },
  {
    id: 'zoom-in',
    action: 'Zoom in (shared screen / video)',
    keys: () => [modKey(), '+'],
  },
  {
    id: 'zoom-out',
    action: 'Zoom out (shared screen / video)',
    keys: () => [modKey(), '-'],
  },
  {
    id: 'zoom-reset',
    action: 'Reset zoom',
    keys: () => [modKey(), '0'],
  },
];

export const SHORTCUTS_ENABLED_KEY = 'ch_problem_shortcuts_enabled';

export const areShortcutsEnabled = () => {
  try {
    const v = localStorage.getItem(SHORTCUTS_ENABLED_KEY);
    if (v === null) return true;
    return v !== 'false';
  } catch {
    return true;
  }
};

export const setShortcutsEnabled = (enabled) => {
  try {
    localStorage.setItem(SHORTCUTS_ENABLED_KEY, enabled ? 'true' : 'false');
  } catch {
    /* ignore */
  }
};

/**
 * Match LeetCode-style bindings.
 * Uses event.code where possible so layout differences don't break Quote / Equal.
 */
export const matchProblemShortcut = (event) => {
  const metaOrCtrl = event.metaKey || event.ctrlKey;
  const alt = event.altKey;
  const code = event.code;
  const key = event.key;

  // Run: ⌘/' or Ctrl+'
  if (metaOrCtrl && !event.altKey && !event.shiftKey && (code === 'Quote' || key === "'")) {
    return 'run';
  }

  // Submit: ⌘/Ctrl + Enter
  if (metaOrCtrl && !event.altKey && (code === 'Enter' || key === 'Enter')) {
    return 'submit';
  }

  // Close tab: ⌥/Alt + W
  if (alt && !metaOrCtrl && (code === 'KeyW' || key === 'w' || key === 'W')) {
    return 'close-tab';
  }

  // Maximize: ⌥/Alt + = or +
  if (
    alt &&
    !metaOrCtrl &&
    (String(code || '').toLowerCase() === 'equal' ||
      code === 'NumpadAdd' ||
      key === '=' ||
      key === '+' ||
      key === '≠' ||
      key === '±')
  ) {
    return 'maximize';
  }

  // Full screen: ⌥/Alt + F
  if (alt && !metaOrCtrl && (code === 'KeyF' || key === 'f' || key === 'F')) {
    return 'fullscreen';
  }

  return null;
};
